/**
 * Utility functions for image manipulation using dotstudioPRO API
 * 
 * The dotstudioPRO API allows image URLs to be resized by appending /width/height
 * to the URL path, and supports optional query params for blur and webp format.
 * 
 * @see https://api.myspotlight.tv/#section/Image-Manipulation
 */

/**
 * Checks if a URL is from a dotstudioPRO image domain
 * 
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL is from a dotstudioPRO image domain
 */
export const isDotstudioProImage = (url) => {
  if (!url || typeof url !== 'string') {return false;}
  
  // List of all known dotstudioPRO image domains
  const dotstudioDomains = [
    'images.dotstudiopro.com',
    'sportstvdspmedia.cachefly.net',
    'cdn.dotstudiopro.com',
    'dprodspimages.s3.amazonaws.com',
  ];
  
  return dotstudioDomains.some(domain => url.includes(domain));
};

/**
 * Optimizes an image URL from dotstudioPRO API by adding size parameters
 * 
 * @param {string} imageUrl - The original image URL from the API
 * @param {Object} options - Configuration options
 * @param {number} options.width - Desired image width
 * @param {number} options.height - Desired image height
 * @param {number} options.blur - Optional blur amount (1-100)
 * @param {boolean} options.webp - Whether to request WebP format
 * @param {boolean} options.highDpi - Whether to double size for high DPI displays
 * @returns {string} Optimized image URL
 */
export const optimizeImageUrl = (imageUrl, options = {}) => {
  // Default options - increased for wider container
  const defaults = {
    width: 400,
    height: 225,
    blur: null,
    webp: true,
    highDpi: window.devicePixelRatio > 1.5,
  };

  // Merge defaults with provided options
  const config = { ...defaults, ...options };
  
  // If not a dotstudioPRO image URL, return original
  if (!imageUrl || !isDotstudioProImage(imageUrl)) {
    return imageUrl;
  }

  try {
    // Parse the URL to handle both path and query parameters correctly
    const url = new URL(imageUrl);
    const baseUrl = url.origin + url.pathname;
    
    // Calculate dimensions, accounting for high DPI if needed
    const width = config.highDpi ? config.width * 2 : config.width;
    const height = config.highDpi ? config.height * 2 : config.height;
    
    // Create the size-optimized URL
    let optimizedUrl = `${baseUrl}/${width}/${height}`;
    
    // Add query parameters if needed
    const params = new URLSearchParams();
    if (config.blur && config.blur >= 1 && config.blur <= 100) {
      params.append('blur', config.blur);
    }
    if (config.webp) {
      params.append('webp', '1');
    }
    
    // Add any existing query parameters from the original URL
    for (const [key, value] of new URLSearchParams(url.search)) {
      if (key !== 'blur' && key !== 'webp') {
        params.append(key, value);
      }
    }
    
    // Append query parameters if any exist
    const queryString = params.toString();
    if (queryString) {
      optimizedUrl += `?${queryString}`;
    }
    
    return optimizedUrl;
  } catch (error) {
    console.error('Error optimizing image URL:', error);
    return imageUrl; // Return original URL if there's an error
  }
};

/**
 * Predefined image size presets for different UI contexts
 */
export const ImageSizePresets = {
  // Channel card thumbnails - increased for wider container
  CHANNEL_CARD_THUMBNAIL: {
    width: 400,
    height: 225,
    webp: true,
  },
  
  // Channel card thumbnails for mobile
  CHANNEL_CARD_THUMBNAIL_MOBILE: {
    width: 300,
    height: 169,
    webp: true,
  },
  
  // Channel detail hero image
  CHANNEL_DETAIL_HERO: {
    width: 1920,
    height: 1080,
    webp: true,
  },
  
  // Category thumbnail - increased for wider container
  CATEGORY_THUMBNAIL: {
    width: 500,
    height: 281,
    webp: true,
  },
  
  // Video thumbnail - increased for wider container
  VIDEO_THUMBNAIL: {
    width: 400,
    height: 225,
    webp: true,
  },
  
  // Profile avatar
  PROFILE_AVATAR: {
    width: 100,
    height: 100,
    webp: true,
  },
  
  // Episode thumbnail for Netflix navigation
  EPISODE_THUMBNAIL: {
    width: 320,
    height: 180,
    webp: true,
  },
  
  // Hover modal thumbnail
  HOVER_MODAL_THUMBNAIL: {
    width: 541,
    height: 304,
    webp: true,
  },
  
  // Related content thumbnail (2:3 portrait poster - issue #20)
  RELATED_CONTENT_THUMBNAIL: {
    width: 320,
    height: 480,
    webp: true,
  },
};

/**
 * Gets the appropriate image size preset based on screen width
 * 
 * @param {string} presetType - The type of preset to get
 * @returns {Object} The appropriate size preset
 */
export const getResponsiveImagePreset = (presetType) => {
  const isMobile = window.innerWidth < 768;
  
  switch (presetType) {
    case 'CHANNEL_CARD':
      return isMobile 
        ? ImageSizePresets.CHANNEL_CARD_THUMBNAIL_MOBILE 
        : ImageSizePresets.CHANNEL_CARD_THUMBNAIL;
    case 'CHANNEL_HERO':
      return ImageSizePresets.CHANNEL_DETAIL_HERO;
    case 'CATEGORY':
      return ImageSizePresets.CATEGORY_THUMBNAIL;
    case 'VIDEO':
      return ImageSizePresets.VIDEO_THUMBNAIL;
    case 'AVATAR':
      return ImageSizePresets.PROFILE_AVATAR;
    case 'EPISODE_THUMBNAIL':
      return ImageSizePresets.EPISODE_THUMBNAIL;
    case 'HOVER_MODAL':
      return ImageSizePresets.HOVER_MODAL_THUMBNAIL;
    case 'RELATED_CONTENT':
      return ImageSizePresets.RELATED_CONTENT_THUMBNAIL;
    default:
      return ImageSizePresets.CHANNEL_CARD_THUMBNAIL;
  }
};

export default {
  optimizeImageUrl,
  isDotstudioProImage,
  ImageSizePresets,
  getResponsiveImagePreset,
};
